import React from 'react';
import { supabase } from '../../lib/supabase';
import { normalizarTexto } from '../../utils/extrato/comum';
import { parseCsv } from '../../utils/extrato/parseCsv';
import { parseOfx, ehOfx } from '../../utils/extrato/parseOfx';
import {
  codigoDaConta, compararCodigos, gerarChaves, montarRegras,
  prepararLinhas, resumir, paraRegistros,
} from '../../utils/extrato/montarImportacao';
import { contasAnaliticas, pendentesDeClassificacao } from '../../utils/contabil/classificacao';
import {
  CODIGO_A_CLASSIFICAR, contasParaConferir, flagConta, situacaoValidacao, resumoFechamento, validacoesAntigasDoPeriodo,
} from '../../utils/contabil/conferencia';
import { ROXO, TEXTO, SUAVE, BORDA, fmtMoeda, fmtData, botaoPrimario, botaoSecundario, Aviso } from './comum';
import EtapaClassificar from './EtapaClassificar';
import EtapaConferir from './EtapaConferir';
import EtapaFechar from './EtapaFechar';
import RecorrentesPainel from './RecorrentesPainel';
import NotasFiscaisPainel from './NotasFiscaisPainel';

// Assistente de fechamento do mês (Contabilidade): 1 Extratos, 2 Classificar,
// 3 Conferir contas, 4 Fechar.
//
// Grava em contabil_extratos + contabil_lancamentos (migração 028). O gatilho
// do banco recusa data anterior ao corte e mês fechado; aqui essas linhas são
// separadas antes, na pré-visualização, para não derrubar o lote inteiro.
//
// Números do balancete: vêm prontos por props (`balancete`, sempre o mensal),
// calculados por utils/contabil/balancete.js — a mesma função da aba Balancete.
// Validações e fechamentos: hook useContabilFechamento, instância única criada
// em Relatorios e compartilhada com a aba Balancete.

const LOTE_GRAVACAO = 500;
const LOTE_CONSULTA = 150; // chaves por consulta .in() — mantém a URL curta

const ultimoDia = (ano, mes) => {
  const d = new Date(Date.UTC(Number(ano), Number(mes), 0)).getUTCDate();
  return `${ano}-${mes}-${String(d).padStart(2, '0')}`;
};

// Termos que identificam o banco no nome da conta contábil.
const TERMOS_BANCO = {
  'Banco Inter': ['inter'],
  'Banco Santander': ['santander'],
  'Banco do Brasil': ['banco do brasil', ' bb'],
  'Banrisul': ['banrisul'],
  'Caixa Econômica Federal': ['caixa economica', 'cef'],
  'BTG Pactual': ['btg'],
  'Bradesco': ['bradesco'],
  'Nubank': ['nubank', 'nu pagamentos'],
  'PagBank': ['pagbank', 'pagseguro'],
  'Mercado Pago': ['mercado pago'],
  'C6 Bank': ['c6'],
  'Itaú': ['itau'],
  'PicPay': ['picpay'],
  'Cora': ['cora'],
  'Sicredi': ['sicredi'],
  'Sicoob': ['sicoob'],
};

function decodificar(buffer) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    // Arquivos de banco brasileiro costumam vir em Windows-1252 / ISO-8859-1.
    return new TextDecoder('windows-1252').decode(buffer);
  }
}

export default function FechamentoMes({
  empresaSlug,
  empresaCnpj,
  filMes,
  filAno,
  mesesNomes,
  planoContas,
  regrasJson,
  inicioLancamentos,
  lancamentosContabeis,
  balancetesFechados,
  balancete,
  fechamento,
  validacoesAntigas,
  isAdmin,
  onRemoverFechamentoAntigo,
  onImportado,
  cardStyle,
  inputStyle,
  labelStyle,
}) {
  const [etapa, setEtapa] = React.useState(1);
  const [extratos, setExtratos] = React.useState([]);
  const [regrasDb, setRegrasDb] = React.useState([]);
  const [carregando, setCarregando] = React.useState(false);
  const [aviso, setAviso] = React.useState(null);

  const [arquivo, setArquivo] = React.useState(null); // { nome, leitura }
  const [contaSel, setContaSel] = React.useState('');
  const [chavesExistentes, setChavesExistentes] = React.useState(null); // Set | null enquanto consulta
  const [gravando, setGravando] = React.useState(false);

  const periodo = `${filAno}-${filMes}`;
  const inicioMes = `${periodo}-01`;
  const fimMes = ultimoDia(filAno, filMes);
  const nomeMes = `${mesesNomes[parseInt(filMes, 10) - 1]}/${filAno}`;

  // ── Dados do banco ──────────────────────────────────────────────────────
  const carregar = React.useCallback(async () => {
    if (!empresaSlug) return;
    setCarregando(true);
    try {
      const [ext, reg] = await Promise.all([
        supabase.from('contabil_extratos')
          .select('id, banco, conta_banco, arquivo_nome, formato, periodo_inicio, periodo_fim, qtd, importado_em')
          .eq('empresa_slug', empresaSlug)
          .order('importado_em', { ascending: false }),
        supabase.from('contabil_regras').select('id, padrao, conta_contabil, sentido, criado_em').eq('empresa_slug', empresaSlug),
      ]);
      const erro = ext.error || reg.error;
      if (erro) throw erro;
      setExtratos(ext.data || []);
      setRegrasDb(reg.data || []);
    } catch (err) {
      console.error('[fechamento-mes] Falha ao carregar extratos ou regras.', err);
      setAviso({ tipo: 'erro', texto: 'Não foi possível carregar os extratos da empresa. Atualize a página; se o problema continuar, acione o suporte.' });
    } finally {
      setCarregando(false);
    }
  }, [empresaSlug]);

  React.useEffect(() => { carregar(); }, [carregar]);

  // Troca de empresa descarta o arquivo em análise.
  React.useEffect(() => { setArquivo(null); setContaSel(''); setAviso(null); }, [empresaSlug]);

  // ── Contas bancárias (1.1.1.x analíticas) ────────────────────────────────
  const contasBanco = React.useMemo(() => {
    const plano = planoContas || [];
    return plano
      .filter(c => String(c.codigo || '').startsWith('1.1.1.'))
      .filter(c => !plano.some(o => String(o.codigo || '').startsWith(`${c.codigo}.`)))
      .map(c => ({ codigo: c.codigo, nome: c.nome, rotulo: `${c.codigo} — ${c.nome}` }))
      .sort((a, b) => compararCodigos(a.codigo, b.codigo));
  }, [planoContas]);

  const fechamentosDb = fechamento.fechamentos;
  const periodosFechados = React.useMemo(
    () => new Set([...fechamentosDb.map(f => f.periodo), ...Object.keys(balancetesFechados || {})]),
    [fechamentosDb, balancetesFechados],
  );
  const mesFechado = periodosFechados.has(periodo);

  const lancMes = React.useMemo(
    () => (lancamentosContabeis || []).filter(l => (l.data || '').slice(0, 7) === periodo),
    [lancamentosContabeis, periodo],
  );

  const situacaoContas = React.useMemo(() => contasBanco.map(c => {
    const doMes = extratos.filter(e => codigoDaConta(e.conta_banco) === c.codigo
      && (e.periodo_inicio || '') <= fimMes && (e.periodo_fim || '') >= inicioMes);
    const algumExtrato = extratos.some(e => codigoDaConta(e.conta_banco) === c.codigo);
    const qtdLanc = lancMes.filter(l => codigoDaConta(l.contaDebito) === c.codigo || codigoDaConta(l.contaCredito) === c.codigo).length;
    // Conta que entra na conta do progresso: não é o caixa físico e tem uso
    // (movimento no mês ou algum extrato já importado).
    const ehCaixa = normalizarTexto(c.nome) === 'caixa';
    const esperaExtrato = !ehCaixa && (qtdLanc > 0 || algumExtrato);
    return { ...c, extratosMes: doMes, qtdLanc, esperaExtrato };
  }), [contasBanco, extratos, lancMes, inicioMes, fimMes]);

  const esperadas = situacaoContas.filter(c => c.esperaExtrato);
  const comExtrato = esperadas.filter(c => c.extratosMes.length > 0);

  const ehAClassificar = (l) => codigoDaConta(l.contaDebito) === CODIGO_A_CLASSIFICAR || codigoDaConta(l.contaCredito) === CODIGO_A_CLASSIFICAR;
  const qtdAClassificar = lancMes.filter(ehAClassificar).length;
  const pctClassificados = lancMes.length
    ? Math.floor(100 * (lancMes.length - qtdAClassificar) / lancMes.length)
    : null;

  // ── Etapa 2: classificação ──────────────────────────────────────────────
  const pendentes = React.useMemo(() => pendentesDeClassificacao(lancamentosContabeis, periodo), [lancamentosContabeis, periodo]);
  const aClassificarArquivo = lancMes.filter(l => !l._db && ehAClassificar(l)).length;
  const contasAnal = React.useMemo(() => contasAnaliticas(planoContas), [planoContas]);

  // ── Etapa 3: conferência das contas ─────────────────────────────────────
  const validacoesPorConta = React.useMemo(() => {
    const m = new Map();
    fechamento.validacoes.forEach(v => m.set(v.conta_codigo, v));
    return m;
  }, [fechamento.validacoes]);

  const itensConferencia = React.useMemo(() => contasParaConferir(balancete.linhas).map(linha => {
    const lancs = (balancete.lancamentosPorConta || {})[linha.codigo] || [];
    const validacao = validacoesPorConta.get(linha.codigo);
    return { codigo: linha.codigo, linha, lancs, validacao, situacao: situacaoValidacao(linha, validacao, lancs), flag: flagConta(linha) };
  }), [balancete, validacoesPorConta]);

  const codigosAntigos = React.useMemo(
    () => validacoesAntigasDoPeriodo(validacoesAntigas, periodo, balancete.linhas.map(l => l.codigo)),
    [validacoesAntigas, periodo, balancete],
  );

  // ── Leitura do arquivo ──────────────────────────────────────────────────
  const sugerirConta = React.useCallback((leitura, identBanco) => {
    const existe = (rot) => contasBanco.find(c => c.codigo === codigoDaConta(rot));
    // 1. O mesmo banco/conta já foi importado antes: repete a conta escolhida.
    if (identBanco) {
      const anterior = extratos.find(e => e.banco === identBanco && existe(e.conta_banco));
      if (anterior) return existe(anterior.conta_banco).rotulo;
    }
    // 2. Nome da conta contábil cita o banco detectado.
    const candidatas = contasBanco.filter(c => normalizarTexto(c.nome) !== 'caixa');
    if (leitura.banco) {
      const termos = TERMOS_BANCO[leitura.banco] || [normalizarTexto(leitura.banco)];
      const achou = candidatas.find(c => termos.some(t => ` ${normalizarTexto(c.nome)}`.includes(t)));
      if (achou) return achou.rotulo;
    }
    // 3. Só uma conta de banco movimento no plano.
    const movimento = candidatas.filter(c => normalizarTexto(c.nome).includes('banco'));
    if (movimento.length === 1) return movimento[0].rotulo;
    return '';
  }, [contasBanco, extratos]);

  const identificacaoBanco = (leitura) => {
    if (!leitura) return null;
    if (leitura.banco && leitura.acctId) return `${leitura.banco} · conta ${leitura.acctId}`;
    if (leitura.banco) return leitura.banco;
    if (leitura.acctId) return `Conta ${leitura.acctId}`;
    return null;
  };

  // PDF não tem estrutura de dados: a leitura é feita por IA no servidor
  // (api/extrato-pdf.js), que devolve as movimentações no mesmo formato do CSV.
  // Daqui em diante o fluxo é o mesmo: conta, regras, prévia e confirmação.
  const [lendoPdf, setLendoPdf] = React.useState(false);
  const lerPdf = async (file) => {
    if (file.size > 3 * 1024 * 1024) {
      setAviso({ tipo: 'erro', texto: 'O PDF tem mais de 3 MB. Exporte um período menor do extrato.' });
      return;
    }
    setLendoPdf(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => resolve(String(leitor.result).split(',')[1] || '');
        leitor.onerror = () => reject(leitor.error);
        leitor.readAsDataURL(file);
      });
      const { data: sessao } = await supabase.auth.getSession();
      const resp = await fetch('/api/extrato-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessao?.session?.access_token || ''}` },
        body: JSON.stringify({ pdf: base64 }),
      });
      const corpo = await resp.json().catch(() => ({}));
      if (!resp.ok || !corpo.leitura) {
        setAviso({ tipo: 'erro', texto: corpo.error || 'Não foi possível ler o PDF. Tente novamente ou importe o extrato em OFX/CSV.' });
        return;
      }
      const leitura = corpo.leitura;
      setArquivo({ nome: file.name, leitura });
      setContaSel(sugerirConta(leitura, identificacaoBanco(leitura)));
      setAviso({
        tipo: corpo.aviso ? 'alerta' : 'info',
        texto: `Extrato lido por IA: ${leitura.transacoes.length} movimentação(ões). ${corpo.aviso || 'Saldo anterior + movimentações conferem com o saldo final do extrato.'} Confira a prévia antes de gravar.`,
      });
    } catch (err) {
      console.error('[fechamento-mes] Falha ao ler o PDF.', err);
      setAviso({ tipo: 'erro', texto: 'Não foi possível ler o PDF. Tente novamente ou importe o extrato em OFX/CSV.' });
    } finally {
      setLendoPdf(false);
    }
  };

  const aoEscolherArquivo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setAviso(null);
    setArquivo(null);
    setChavesExistentes(null);
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['csv', 'ofx', 'txt', 'pdf'].includes(ext)) {
      setAviso({ tipo: 'erro', texto: 'Formato não aceito. Envie o extrato em PDF, OFX ou CSV.' });
      return;
    }
    if (ext === 'pdf') { await lerPdf(file); return; }
    try {
      const texto = decodificar(await file.arrayBuffer());
      const leitura = (ext === 'ofx' || ehOfx(texto)) ? parseOfx(texto) : parseCsv(texto);
      setArquivo({ nome: file.name, leitura });
      setContaSel(sugerirConta(leitura, identificacaoBanco(leitura)));
    } catch (err) {
      console.error('[fechamento-mes] Falha ao ler o extrato.', err);
      // As mensagens dos leitores já são escritas para a tela; qualquer outra vira genérica.
      const msg = err && /^(O arquivo|Não foi possível identificar|Nenhuma movimentação)/.test(err.message || '')
        ? err.message
        : 'Não foi possível ler o arquivo. Confira se é o extrato exportado pelo banco em CSV ou OFX.';
      setAviso({ tipo: 'erro', texto: msg });
    }
  };

  // Consulta quais chaves do arquivo já estão gravadas (refaz ao trocar a conta,
  // porque a conta faz parte da chave do CSV).
  React.useEffect(() => {
    if (!arquivo || !contaSel || !empresaSlug) { setChavesExistentes(null); return undefined; }
    let cancelado = false;
    setChavesExistentes(null);
    (async () => {
      try {
        const chaves = [...new Set(gerarChaves(arquivo.leitura.transacoes, { contaBanco: contaSel, acctId: arquivo.leitura.acctId }))];
        const achadas = new Set();
        for (let i = 0; i < chaves.length; i += LOTE_CONSULTA) {
          const { data, error } = await supabase.from('contabil_lancamentos')
            .select('chave_dedup')
            .eq('empresa_slug', empresaSlug)
            .in('chave_dedup', chaves.slice(i, i + LOTE_CONSULTA));
          if (error) throw error;
          (data || []).forEach(r => achadas.add(r.chave_dedup));
        }
        if (!cancelado) setChavesExistentes(achadas);
      } catch (err) {
        console.error('[fechamento-mes] Falha ao consultar lançamentos já importados.', err);
        if (!cancelado) {
          setAviso({ tipo: 'erro', texto: 'Não foi possível conferir os lançamentos já importados. Tente novamente em instantes.' });
        }
      }
    })();
    return () => { cancelado = true; };
  }, [arquivo, contaSel, empresaSlug]);

  const regras = React.useMemo(() => montarRegras(regrasDb, regrasJson), [regrasDb, regrasJson]);

  const linhas = React.useMemo(() => {
    if (!arquivo || !contaSel || !chavesExistentes) return null;
    return prepararLinhas({
      transacoes: arquivo.leitura.transacoes,
      contaBanco: contaSel,
      acctId: arquivo.leitura.acctId,
      regras,
      inicio: inicioLancamentos,
      periodosFechados,
      chavesExistentes,
    }).sort((a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : 0));
  }, [arquivo, contaSel, chavesExistentes, regras, inicioLancamentos, periodosFechados]);

  const resumo = linhas ? resumir(linhas) : null;

  const descartar = () => { setArquivo(null); setContaSel(''); setChavesExistentes(null); };

  // ── Gravação ────────────────────────────────────────────────────────────
  const gravar = async () => {
    if (!linhas || !resumo || resumo.novas === 0 || gravando) return;
    setGravando(true);
    setAviso(null);
    const leitura = arquivo.leitura;
    let extratoId = null;
    let gravados = 0;
    const total = resumo.novas;
    try {
      const { data: ext, error: errExt } = await supabase.from('contabil_extratos').insert({
        empresa_slug: empresaSlug,
        banco: identificacaoBanco(leitura),
        conta_banco: contaSel,
        arquivo_nome: arquivo.nome,
        formato: leitura.formato,
        periodo_inicio: leitura.periodoInicio,
        periodo_fim: leitura.periodoFim,
        qtd: total,
      }).select('id').single();
      if (errExt) throw errExt;
      extratoId = ext.id;

      const registros = paraRegistros(linhas, { empresaSlug, extratoId });
      for (let i = 0; i < registros.length; i += LOTE_GRAVACAO) {
        const { data, error } = await supabase.from('contabil_lancamentos')
          .upsert(registros.slice(i, i + LOTE_GRAVACAO), { onConflict: 'empresa_slug,chave_dedup', ignoreDuplicates: true })
          .select('id');
        if (error) throw error;
        gravados += (data || []).length;
      }

      if (gravados !== total) {
        // Outra pessoa importou parte do mesmo arquivo nesse intervalo: o
        // upsert ignorou as repetidas. O cabeçalho registra o que entrou de fato.
        await supabase.from('contabil_extratos').update({ qtd: gravados }).eq('id', extratoId);
      }

      const classificadas = linhas.filter(l => l.situacao === 'nova' && l.contrapartida).length;
      setAviso({
        tipo: 'sucesso',
        texto: `Extrato gravado: ${gravados} lançamento(s) incluído(s) em ${contaSel}. `
          + `${Math.min(classificadas, gravados)} classificado(s) pelas regras e ${Math.max(gravados - classificadas, 0)} a classificar.`,
      });
      descartar();
      await carregar();
      if (onImportado) onImportado();
    } catch (err) {
      console.error('[fechamento-mes] Falha ao gravar o extrato.', { err, extratoId, gravados, total });
      if (extratoId && gravados === 0) {
        // Nada entrou: remove o cabeçalho para não deixar extrato vazio na lista.
        const { error: errDel } = await supabase.from('contabil_extratos').delete().eq('id', extratoId);
        if (errDel) console.error('[fechamento-mes] Falha ao remover o cabeçalho do extrato sem lançamentos.', errDel);
      } else if (extratoId) {
        await supabase.from('contabil_extratos').update({ qtd: gravados }).eq('id', extratoId);
      }
      setAviso({
        tipo: 'erro',
        texto: gravados > 0
          ? `Parte dos lançamentos foi gravada (${gravados} de ${total}). Importe o mesmo arquivo novamente para concluir: as linhas já gravadas serão reconhecidas como duplicadas.`
          : 'Não foi possível gravar o extrato. Tente novamente; se o problema continuar, acione o suporte.',
      });
      await carregar();
      if (onImportado && gravados > 0) onImportado();
    } finally {
      setGravando(false);
    }
  };

  // ── Interface ───────────────────────────────────────────────────────────
  // ── Etapa 4: fechamento ─────────────────────────────────────────────────
  const extratosFaltando = React.useMemo(
    () => esperadas.filter(c => c.extratosMes.length === 0).map(c => c.rotulo),
    [esperadas],
  );
  const resumoFech = React.useMemo(() => resumoFechamento({
    itens: itensConferencia,
    aClassificar: qtdAClassificar,
    totalDebitos: balancete.totalDebitos,
    totalCreditos: balancete.totalCreditos,
    extratosFaltando,
    qtdLinhas: balancete.linhas.length,
  }), [itensConferencia, qtdAClassificar, balancete, extratosFaltando]);

  const fechamentoAtual = fechamentosDb.find(f => f.periodo === periodo) || null;
  const fechamentoAntigo = (balancetesFechados || {})[periodo] || null;

  const fecharMes = async () => {
    if (!resumoFech.podeFechar || mesFechado) return;
    setAviso(null);
    const r = await fechamento.fechar({
      periodo,
      fechadoEm: new Date().toISOString(),
      linhas: balancete.linhas,
      totalDebitos: balancete.totalDebitos,
      totalCreditos: balancete.totalCreditos,
      contasValidadas: resumoFech.validadas,
      extratosFaltando,
    });
    setAviso(r.ok
      ? { tipo: 'sucesso', texto: `${nomeMes} fechado. O mês fica bloqueado para inclusão, alteração e exclusão de lançamentos.` }
      : { tipo: 'erro', texto: 'Não foi possível fechar o mês. Atualize a página e tente novamente; se o problema continuar, acione o suporte.' });
  };

  const reabrirMes = async (justificativa) => {
    if (!isAdmin) return;
    setAviso(null);
    const r = await fechamento.reabrir(justificativa, fechamentoAtual ? null : fechamentoAntigo);
    if (!r.ok) {
      setAviso({ tipo: 'erro', texto: 'Não foi possível reabrir o período. Confira se o seu perfil é de administrador e tente novamente.' });
      return;
    }
    // Fechamento feito pela tela antiga também sai do contabilConfig.
    if (fechamentoAntigo && onRemoverFechamentoAntigo) onRemoverFechamentoAntigo(periodo);
    setAviso({ tipo: 'sucesso', texto: `${nomeMes} reaberto. A justificativa foi registrada.` });
  };

  const recarregarTudo = () => { carregar(); if (onImportado) onImportado(); };

  const etapas = [
    { n: 1, titulo: 'Extratos', valor: esperadas.length ? `${comExtrato.length}/${esperadas.length}` : '—', completa: esperadas.length > 0 && comExtrato.length === esperadas.length },
    { n: 2, titulo: 'Classificados', valor: pctClassificados === null ? '—' : `${pctClassificados}%`, completa: lancMes.length > 0 && qtdAClassificar === 0 },
    { n: 3, titulo: 'Contas validadas', valor: itensConferencia.length ? `${resumoFech.validadas}/${resumoFech.contasTotal}` : '—', completa: itensConferencia.length > 0 && resumoFech.validadas === resumoFech.contasTotal },
    { n: 4, titulo: 'Fechamento', valor: mesFechado ? 'Fechado' : resumoFech.podeFechar ? 'Pronto para fechar' : `${resumoFech.bloqueios.length} pendência(s)`, completa: mesFechado },
  ];

  const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, color: SUAVE, fontWeight: 700, textTransform: 'uppercase', borderBottom: `1px solid ${BORDA}`, position: 'sticky', top: 0, background: '#ffffff' };
  const td = { padding: '6px 8px', fontSize: 12, color: TEXTO, borderBottom: '1px solid #f1eef7', verticalAlign: 'top' };

  const corSituacao = { nova: '#047857', duplicada: '#b45309', ignorada: '#b91c1c' };
  const rotuloSituacao = { nova: 'Nova', duplicada: 'Duplicada', ignorada: 'Ignorada' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ ...cardStyle, padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <h3 style={{ color: TEXTO, margin: 0 }}>Fechamento do mês — {nomeMes}</h3>
          {mesFechado && <span style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: 20, padding: '3px 12px', fontSize: 11, color: '#047857', fontWeight: 700 }}>MÊS FECHADO</span>}
        </div>
        {/* Barra de progresso: cada etapa é clicável */}
        <div role="tablist" aria-label="Etapas do fechamento" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {etapas.map(et => {
            const ativa = etapa === et.n;
            return (
              <button key={et.n} role="tab" aria-selected={ativa} onClick={() => setEtapa(et.n)} style={{
                textAlign: 'left', cursor: 'pointer', borderRadius: 8, padding: '8px 12px',
                background: ativa ? 'rgba(124,58,237,0.12)' : '#f7f5fb',
                border: `1px solid ${ativa ? ROXO : BORDA}`,
                borderBottom: `3px solid ${et.completa ? '#10b981' : ativa ? ROXO : BORDA}`,
              }}>
                <div style={{ fontSize: 10, color: SUAVE, fontWeight: 700, textTransform: 'uppercase' }}>{et.n}. {et.titulo}</div>
                <div style={{ fontSize: 15, color: et.completa ? '#047857' : TEXTO, fontWeight: 700, marginTop: 2 }}>{et.valor}</div>
              </button>
            );
          })}
        </div>
      </div>

      <Aviso aviso={aviso} onFechar={() => setAviso(null)} />

      {fechamento.erro && (
        <Aviso aviso={{ tipo: 'erro', texto: 'Não foi possível carregar as validações e os fechamentos deste período. Atualize a página; se o problema continuar, acione o suporte.' }} />
      )}

      {etapa === 2 && (
        <EtapaClassificar
          empresaSlug={empresaSlug}
          pendentes={pendentes}
          aClassificarArquivo={aClassificarArquivo}
          contas={contasAnal}
          regrasDb={regrasDb}
          regras={regras}
          qtdRegrasArquivo={(regrasJson || []).filter(r => r && r.padrao && r.contaContabil).length}
          mesFechado={mesFechado}
          nomeMes={nomeMes}
          onAlterado={recarregarTudo}
          onIrEtapa={setEtapa}
          setAviso={setAviso}
          cardStyle={cardStyle}
          inputStyle={inputStyle}
          labelStyle={labelStyle}
        />
      )}

      {etapa === 3 && (
        <EtapaConferir
          itens={itensConferencia}
          totalDebitos={balancete.totalDebitos}
          totalCreditos={balancete.totalCreditos}
          aClassificar={qtdAClassificar}
          mesFechado={mesFechado}
          nomeMes={nomeMes}
          fechamento={fechamento}
          codigosAntigos={codigosAntigos}
          empresaSlug={empresaSlug}
          contas={contasAnal}
          inicioMes={inicioMes < (inicioLancamentos || '') ? inicioLancamentos : inicioMes}
          fimMes={fimMes}
          onLancado={onImportado}
          onIrEtapa={setEtapa}
          setAviso={setAviso}
          cardStyle={cardStyle}
          inputStyle={inputStyle}
          labelStyle={labelStyle}
        />
      )}

      {etapa === 4 && (
        <EtapaFechar
          resumo={resumoFech}
          totalDebitos={balancete.totalDebitos}
          totalCreditos={balancete.totalCreditos}
          mesFechado={mesFechado}
          fechamentoAtual={fechamentoAtual}
          fechamentoAntigo={fechamentoAntigo}
          isAdmin={isAdmin}
          onFechar={fecharMes}
          onReabrir={reabrirMes}
          onIrEtapa={setEtapa}
          nomeMes={nomeMes}
          cardStyle={cardStyle}
          inputStyle={inputStyle}
          labelStyle={labelStyle}
        />
      )}

      {/* Recorrentes: nas etapas 1 e 3, para gerar ao abrir qualquer competência
          e aparecer junto da conferência das contas. */}
      {(etapa === 1 || etapa === 3) && (
        <RecorrentesPainel
          empresaSlug={empresaSlug}
          periodo={periodo}
          nomeMes={nomeMes}
          inicioLancamentos={inicioLancamentos}
          mesFechado={mesFechado}
          lancamentosContabeis={lancamentosContabeis}
          contas={contasAnal}
          onGerado={onImportado}
          setAviso={setAviso}
          cardStyle={cardStyle}
          inputStyle={inputStyle}
          labelStyle={labelStyle}
        />
      )}

      {etapa === 1 && (
        <>
          {/* Contas bancárias e situação do mês */}
          <div style={cardStyle}>
            <h4 style={{ color: TEXTO, margin: '0 0 4px 0' }}>Contas bancárias</h4>
            <p style={{ fontSize: 12, color: SUAVE, margin: '0 0 12px 0' }}>
              Extratos importados que cobrem {nomeMes}. Contas sem movimento e sem extrato anterior não entram na contagem.
            </p>
            {carregando && extratos.length === 0 ? (
              <div style={{ fontSize: 13, color: SUAVE }}>Carregando…</div>
            ) : situacaoContas.length === 0 ? (
              <div style={{ fontSize: 13, color: SUAVE }}>Nenhuma conta 1.1.1 cadastrada no plano de contas desta empresa.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {situacaoContas.map(c => {
                  const tem = c.extratosMes.length > 0;
                  const cor = tem ? '#047857' : c.esperaExtrato ? '#b45309' : SUAVE;
                  const status = tem ? 'Extrato importado' : c.esperaExtrato ? 'Extrato pendente' : 'Sem movimento';
                  return (
                    <div key={c.codigo} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', border: `1px solid ${BORDA}`, borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: TEXTO, fontWeight: 600 }}>{c.rotulo}</div>
                        <div style={{ fontSize: 11, color: SUAVE, marginTop: 2 }}>
                          {c.qtdLanc} lançamento(s) no mês
                          {c.extratosMes.map(e => (
                            <span key={e.id}> · {e.arquivo_nome || 'Extrato'} ({fmtData(e.periodo_inicio)} a {fmtData(e.periodo_fim)}, {e.qtd ?? 0} linha(s))</span>
                          ))}
                        </div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: cor, whiteSpace: 'nowrap' }}>{status}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <NotasFiscaisPainel
            empresaSlug={empresaSlug}
            empresaCnpj={empresaCnpj}
            periodo={periodo}
            nomeMes={nomeMes}
            inicioLancamentos={inicioLancamentos}
            periodosFechados={periodosFechados}
            contas={contasAnal}
            lancamentosContabeis={lancamentosContabeis}
            onLancado={onImportado}
            setAviso={setAviso}
            cardStyle={cardStyle}
            inputStyle={inputStyle}
          />

          {/* Importação */}
          <div style={cardStyle}>
            <h4 style={{ color: TEXTO, margin: '0 0 4px 0' }}>Importar extrato</h4>
            <p style={{ fontSize: 12, color: SUAVE, margin: '0 0 12px 0' }}>
              Extrato exportado pelo banco em PDF, OFX ou CSV. O PDF é lido por IA; confira a prévia. Nada é gravado antes da sua confirmação.
            </p>

            {!inicioLancamentos ? (
              <Aviso aviso={{ tipo: 'info', texto: 'A data de início da escrituração desta empresa não está configurada. A importação fica indisponível até essa configuração.' }} />
            ) : lendoPdf ? (
              <div role="status" style={{ fontSize: 13, color: SUAVE }}>Lendo o PDF do extrato… isso pode levar até um minuto.</div>
            ) : !arquivo ? (
              <label style={{ ...botaoPrimario, display: 'inline-block' }}>
                Selecionar arquivo (PDF, OFX ou CSV)
                <input type="file" accept=".pdf,.csv,.ofx,.txt" style={{ display: 'none' }} onChange={aoEscolherArquivo} />
              </label>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontSize: 13, color: TEXTO }}>
                  <strong>{arquivo.nome}</strong>
                  <span style={{ color: SUAVE }}> · {arquivo.leitura.formato.toUpperCase()} · {identificacaoBanco(arquivo.leitura) || 'Banco não identificado'}</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 420 }}>
                  <label htmlFor="fm-conta-banco" style={labelStyle}>Conta contábil do banco</label>
                  <select id="fm-conta-banco" style={inputStyle} value={contaSel} onChange={e => setContaSel(e.target.value)}>
                    <option value="">Selecione a conta</option>
                    {contasBanco.map(c => <option key={c.codigo} value={c.rotulo}>{c.rotulo}</option>)}
                  </select>
                </div>

                {!contaSel && <div style={{ fontSize: 12, color: '#b45309' }}>Selecione a conta contábil do banco para ver a pré-visualização.</div>}
                {contaSel && !linhas && <div style={{ fontSize: 12, color: SUAVE }}>Conferindo lançamentos já importados…</div>}

                {resumo && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
                      {[
                        ['Linhas no arquivo', resumo.total, TEXTO],
                        ['Período', `${fmtData(resumo.periodoInicio)} a ${fmtData(resumo.periodoFim)}`, TEXTO],
                        ['Classificadas pelas regras', resumo.classificadas, '#047857'],
                        ['A classificar', resumo.aClassificar, '#b45309'],
                        ['Duplicadas', resumo.duplicadas, SUAVE],
                        ['Ignoradas', resumo.ignoradas, resumo.ignoradas ? '#b91c1c' : SUAVE],
                      ].map(([rot, val, cor]) => (
                        <div key={rot} style={{ background: '#f7f5fb', border: `1px solid ${BORDA}`, borderRadius: 8, padding: '8px 10px' }}>
                          <div style={{ fontSize: 10, color: SUAVE, fontWeight: 700, textTransform: 'uppercase' }}>{rot}</div>
                          <div style={{ fontSize: 14, color: cor, fontWeight: 700, marginTop: 2 }}>{val}</div>
                        </div>
                      ))}
                    </div>

                    {Object.keys(resumo.motivosIgnoradas).length > 0 && (
                      <div style={{ fontSize: 12, color: '#b91c1c' }}>
                        {Object.entries(resumo.motivosIgnoradas).map(([motivo, n]) => (
                          <div key={motivo}>{n} linha(s) ignorada(s): {motivo}.</div>
                        ))}
                      </div>
                    )}

                    <div style={{ maxHeight: 360, overflow: 'auto', border: `1px solid ${BORDA}`, borderRadius: 8 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={th}>Data</th>
                            <th style={th}>Descrição</th>
                            <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                            <th style={th}>Contrapartida</th>
                            <th style={th}>Situação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {linhas.map((l, i) => (
                            <tr key={`${l.chave}-${i}`}style={{ opacity: l.situacao === 'nova' ? 1 : 0.6 }}>
                              <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(l.data)}</td>
                              <td style={td}>{l.descricao}</td>
                              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', color: l.valor > 0 ? '#047857' : '#b91c1c' }}>{fmtMoeda(l.valor)}</td>
                              <td style={td}>{l.contrapartida || <span style={{ color: '#b45309' }}>A classificar</span>}</td>
                              <td style={{ ...td, whiteSpace: 'nowrap', color: corSituacao[l.situacao] }} title={l.motivo || undefined}>
                                {rotuloSituacao[l.situacao]}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    onClick={gravar}
                    disabled={!resumo || resumo.novas === 0 || gravando}
                    style={{ ...botaoPrimario, opacity: !resumo || resumo.novas === 0 || gravando ? 0.5 : 1, cursor: !resumo || resumo.novas === 0 || gravando ? 'not-allowed' : 'pointer' }}
                  >
                    {gravando ? 'Gravando…' : `Gravar ${resumo ? resumo.novas : 0} lançamento(s)`}
                  </button>
                  <button onClick={descartar} disabled={gravando} style={botaoSecundario}>Descartar arquivo</button>
                  {resumo && resumo.novas === 0 && (
                    <span style={{ fontSize: 12, color: SUAVE }}>Nenhum lançamento novo para gravar neste arquivo.</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
